import { createAsyncThunk } from "@reduxjs/toolkit";
import { createAction } from "@reduxjs/toolkit";
import {
  getLayers as getLayersApi,
  updateArcgisToken as updateArcgisTokenApi,
  sendFeaturesChange,
  getTerrains as getTerrainsApi,
  submitDataApi,
} from "../api";
import { ApplyFeatureData, ChangeData } from "../../interfaces/change-data";
import { SubmitDataParams } from "../reducers/upload-file";
import { TerrainResponse } from "../../interfaces/terrain";
import {
  AsyncScreenshotActionPayload,
  ChangeDetailsObjectFromArcgisAsyncPayload,
  ChangeDetailsObjectFromArcgisPayload,
  FocusOnFeatureParams,
  MapMode,
  MapSection,
  ScreenshotParams,
  ScreenshotParamsPayload,
  ScreenshotResult,
} from "../reducers/map";
import { Layer } from "../../interfaces/layer";
import { AppDispatch } from "../store";
import { StageType } from "../../interfaces/utilities";
import { OperationType } from "../../components/features/master-utilities/geometry-editor-window/geometry-editor-window";
import Graphic from "@arcgis/core/Graphic";

export const initializeMap = createAction<
  { section: MapSection; mode: MapMode },
  "INITIALIZE_MAP"
>("INITIALIZE_MAP");

export const focusOnSelectedEntity = createAction("FOCUS_ON_SELECTED_ENTITY");
export const zoomIn = createAction("ZOOM_IN");
export const zoomOut = createAction("ZOOM_OUT");
export const polylineOn = createAction("POLYLINE_ON");
export const polylineOff = createAction("POLYLINE_OFF");
export const takeScreenshot = createAction<
  ScreenshotParamsPayload,
  "TAKE_SCREENSHOT"
>("TAKE_SCREENSHOT");
export const focusOnFeature = createAction<
  FocusOnFeatureParams,
  "FOCUS_ON_FEATURE"
>("FOCUS_ON_FEATURE");
export const unfocusOnFeature = createAction("UNFOCUS_ON_FEATURE");
export const extentEstateEn = createAction<
  { estateEn: number; highlightFeature?: boolean },
  "EXTENT_ESTATE_EN"
>("EXTENT_ESTATE_EN");
export const setPlotColors = createAction("SET_PLOT_COLORS");
export const highlightSelectedObject = createAction<
  { objectId: number; layer: Layer },
  "HIGHLIGHT_SELECTED_OBJECT"
>("HIGHLIGHT_SELECTED_OBJECT");
export const takeScreenshotAsyncAction = createAction<
  AsyncScreenshotActionPayload,
  "TAKE_SCREENSHOT_ASYNC"
>("TAKE_SCREENSHOT_ASYNC");
export const takeScreenshotAsync = (data: ScreenshotParams[]) => {
  return (dispatch: AppDispatch): Promise<ScreenshotResult[]> => {
    return new Promise((resolve, reject) => {
      dispatch(
        takeScreenshotAsyncAction({
          data,
          deferred: { resolve, reject },
        }),
      );
    });
  };
};
export const changeDetailsObjectFromArcgis =
  createAction<ChangeDetailsObjectFromArcgisPayload>(
    "CHANGE_DETAILS_OBJECT_FROM_ARCGIS",
  );
export const changeDetailsObjectFromArcgisAsyncAction =
  createAction<ChangeDetailsObjectFromArcgisAsyncPayload>(
    "CHANGE_DETAILS_OBJECT_FROM_ARCGIS_ASYNC",
  );
export const changeDetailsObjectFromArcgisAsync = (
  payload: ChangeDetailsObjectFromArcgisPayload,
) => {
  return (dispatch: AppDispatch) => {
    return new Promise<void>((resolve, reject) => {
      dispatch(
        changeDetailsObjectFromArcgisAsyncAction({
          ...payload,
          deferred: { resolve, reject },
        }),
      );
    });
  };
};
export const setConvertedFeatures = createAction("SET_CONVERTED_FEATURES");
export const setObjectVisibility = createAction(
  "SET_OBJECT_VISIBILITY",
  (objectId: string, visible: boolean) => ({
    payload: { objectId, visible },
  }),
);
export const removeAllTerritory = createAction<{ stage: StageType }>(
  "REMOVE_ALL_TERRITORY",
);
export const removeTerritoryObject = createAction<{ objectId: string }>(
  "REMOVE_TERRITORY_OBJECT",
);
export const triggerGeometryOperation = createAction<OperationType>(
  "TRIGGER_GEOMETRY_OPERATION",
);
export const setFileLayerVisibility = createAction<{
  fileName: string;
  visible: boolean;
  stage: StageType;
}>("SET_FILE_LAYER_VISIBILITY");
export const checkStageTerritoryFileIntersection = createAction<{
  graphic: Graphic;
  stage: StageType;
  resolve: (result: boolean) => void;
}>("CHECK_STAGE_INTERSECTION_BY_STAGE");
export const checkStageTerritoryFileIntersectionAsync = (
  dispatch: AppDispatch,
  graphic: Graphic,
  stage: StageType,
): Promise<boolean> =>
  new Promise((resolve) => {
    dispatch(checkStageTerritoryFileIntersection({ graphic, stage, resolve }));
  });
export const clearSelectedGraphic = createAction("CLEAR_LAST_SELECTED_GRAPHIC");
// export const getLayersDescription = createAsyncThunk(
//     'layer/getLayersDescription',
//     async () => {
//       const response = await request(arcgisBaseURL + '/arcgis/rest/services/Sur1/Sur/FeatureServer/layers?f=pjson')
//       return response;
//     },
// )
export const getLayers = createAsyncThunk("layer/getLayers", getLayersApi);
export const updateArcgisToken = createAsyncThunk(
  "layer/updateArcgisToken",
  async (host: string) => updateArcgisTokenApi(host),
);
export const sendChanges = createAsyncThunk(
  "map/sendChanges",
  async (changeData: ChangeData) => {
    const response = await sendFeaturesChange(changeData);
    return response;
  },
);
export const applyFeatureChange = createAction<
  ApplyFeatureData,
  "APPLY_FEATURE_CHANGE"
>("APPLY_FEATURE_CHANGE");
export const drawBufferDistance = createAction("APPLY_BUFFER_DISTANCE");
export const deleteBufferDistance = createAction("DELETE_BUFFER_DISTANCE");
export const submitData = createAsyncThunk(
  "upload/uploadFiles",
  async (params: SubmitDataParams, { rejectWithValue }) => {
    try {
      return await submitDataApi(params);
    } catch (error: any) {
      return rejectWithValue(error.message || "Unknown error occurred");
    }
  },
);
export const checkInfrastructureAvailability = createAction<{
  resolve: () => void;
}>("CHECK_INFRASTRUCTURE_AVAILABILITY");
export const checkInfrastructureAvailabilityAsync = (
  dispatch: AppDispatch,
): Promise<void> => {
  return new Promise<void>((resolve) => {
    dispatch(checkInfrastructureAvailability({ resolve }));
  });
};

export const getTerrains = createAsyncThunk<TerrainResponse[]>(
  "terrain/getTerrains",
  getTerrainsApi,
);
export const drawVertices = createAction("DRAW_VERTICES");
